import { CallLog, PostAnalysisSegment, TextLog, VoiceMailLog } from '@/interfaces/ILogs';
import { RequestData } from '@/interfaces/ICallInformation';
import { LastEvaluatedKey } from '@/types/TCRM';
import apiClient from '@/utils/apiClient';
import { UpdateResponsePayload } from '@/types/TInbox';

const agentPrefix = '/agent/:agentId';
const DETAILED_CALL_LOGS_API_PATH = '/api/v2/receptionist-dashboard/call-logs-detailed';
const CALL_LOGS_API_PATH = `/api/v2/receptionist-dashboard${agentPrefix}/call-logs`;
const GET_CALL_RECORDING_API_PATH = '/api/v2/receptionist-dashboard/call-recording';
const USER_TIMEZONE_API_PATH = `/api/v2/receptionist-dashboard${agentPrefix}/user-timezone`;
const TEXT_LOGS_API_PATH = `/api/v2/receptionist-dashboard${agentPrefix}/text-logs`;
const VOICEMAIL_LOGS_API_PATH = `/api/v2/receptionist-dashboard${agentPrefix}/voicemail-logs`;
const DOWNLOAD_CALL_LOGS = `/api/v2/receptionist-dashboard${agentPrefix}/download/call-logs`;
const CALL_LOG_BY_CALL_SID = `/api/v2/receptionist-dashboard${agentPrefix}/call-log`;
const UPDATE_POST_CALL_ANALYSIS_PATH = `/api/v2/receptionist-dashboard${agentPrefix}/update-post-call-analysis`;
const COMBINED_WEEKLY_CALL_LOGS_PATH = '/api/v2/receptionist-dashboard/combined-weekly-call-logs';
const COMBINED_WEEKLY_TEXT_LOGS_PATH = '/api/v2/receptionist-dashboard/combined-weekly-text-logs';

export const getCallLogs = async (
	agentId: string,
	queryParam: {
		limit: number | null;
		lastKey?: LastEvaluatedKey;
		startDate?: number | null;
		endDate?: number | null;
		from?: string | null;
	},
): Promise<{
	callLogs: CallLog[];
	lastKey: LastEvaluatedKey | undefined;
}> => {
	let queryParamString = '?';

	if (queryParam.limit) {
		queryParamString += `limit=${queryParam.limit}`;
	}

	if (queryParam.lastKey) {
		queryParamString += `&lastKey=${JSON.stringify(queryParam.lastKey)}`;
	}

	if (queryParam.startDate) {
		queryParamString += `&startDate=${queryParam.startDate}`;
	}

	if (queryParam.endDate) {
		queryParamString += `&endDate=${queryParam.endDate}`;
	}

	if (queryParam.from) {
		queryParamString += `&from=${encodeURIComponent(queryParam.from)}`;
	}

	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${CALL_LOGS_API_PATH.replace(':agentId', agentId)}${queryParamString}`,
	});

	return {
		lastKey: response.lastEvaluatedKey,
		callLogs: response.callLogs,
	};
};

export const getCallRecording = async (callSid: string) => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${GET_CALL_RECORDING_API_PATH}/${callSid}`,
		isBlobResponse: true,
	});

	const blob = new Blob([response], { type: 'audio/mpeg' });
	const url = URL.createObjectURL(blob);

	return { callSid, url };
};

export const deleteCallLog = async ({
	callSid,
	date,
	agentId,
}: {
	callSid: string;
	date: string;
	agentId: string;
}): Promise<{ success: boolean }> => {
	const response = await apiClient.request({
		method: 'DELETE',
		endPoint: `${CALL_LOGS_API_PATH.replace(':agentId', agentId)}/${callSid}?date=${date}`,
	});

	return response;
};

export const updateCallLog = async ({
	callSid,
	date,
	data,
	agentId,
}: {
	callSid: string;
	date: string;
	data: any;
	agentId: string;
}): Promise<{ success: boolean; data: any }> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: `${CALL_LOGS_API_PATH.replace(':agentId', agentId)}/${callSid}?date=${date}`,
		body: data,
	});

	return response;
};

export const updateUserTimeZone = async (payload: {
	timeZone: string;
	agentId: string;
}): Promise<{ success: boolean }> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'PUT',
		endPoint: `${USER_TIMEZONE_API_PATH.replace(':agentId', agentId)}`,
		body,
	});

	return response;
};

export const getTextLogs = async (agentId: string): Promise<{ textLogs: TextLog[] }> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${TEXT_LOGS_API_PATH.replace(':agentId', agentId)}`,
	});

	return response;
};

export const deleteTextLog = async (payload: {
	textLogDate: string;
	agentId: string;
}): Promise<{ success: boolean }> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'DELETE',
		endPoint: `${TEXT_LOGS_API_PATH.replace(':agentId', agentId)}`,
		body,
	});

	return response;
};

export const getVoiceMailLogs = async (
	agentId: string,
): Promise<{ voiceMailLogs: VoiceMailLog[] }> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${VOICEMAIL_LOGS_API_PATH.replace(':agentId', agentId)}`,
	});

	return response;
};

export const deleteVoiceMailLog = async (payload: {
	voiceMailLogDate: string;
	agentId: string;
}): Promise<{ success: boolean }> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'DELETE',
		endPoint: `${VOICEMAIL_LOGS_API_PATH.replace(':agentId', agentId)}`,
		body,
	});

	return response;
};

export const fetchCallInformation = async (shareableLink: string): Promise<{ data: CallLog }> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${CALL_LOGS_API_PATH.replace('/agent/:agentId', '')}/${shareableLink}`,
	});

	return response;
};

export const fetchDetailedCallInformation = async (
	body: RequestData,
): Promise<{ data: CallLog }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: `${DETAILED_CALL_LOGS_API_PATH}`,
		body,
	});

	return response;
};

export const downloadCallLogs = async (queryParam: {
	startDate?: number | null;
	endDate?: number | null;
	isNoviUser: boolean;
	agentId: string;
}): Promise<void> => {
	let queryParamString = '?';

	if (queryParam.startDate) {
		queryParamString += `&startDate=${queryParam.startDate}`;
	}

	if (queryParam.endDate) {
		queryParamString += `&endDate=${queryParam.endDate}`;
	}

	if (queryParam.isNoviUser) {
		queryParamString += `&novi=${queryParam.isNoviUser}`;
	}

	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${DOWNLOAD_CALL_LOGS.replace(':agentId', queryParam.agentId)}${queryParamString}`,
	});

	const blob = new Blob([response.callReport], { type: 'text/csv' });
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = `call_logs_${new Date().toISOString()}.csv`;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	window.URL.revokeObjectURL(url);
};

export const getCallLogByCallSid = async ({
	agentId,
	callSid,
}: {
	agentId: string;
	callSid: string;
}): Promise<{ success: boolean; data?: CallLog }> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${CALL_LOG_BY_CALL_SID.replace(':agentId', agentId)}/${callSid}`,
	});

	return response;
};

export const updatePostCallAnalysis = async (payload: {
	postCallAnalysis: PostAnalysisSegment[];
	agentId: string;
}): Promise<{ success: boolean }> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'PUT',
		endPoint: `${UPDATE_POST_CALL_ANALYSIS_PATH.replace(':agentId', agentId)}`,
		body,
	});

	return response;
};

export const getCombinedWeeklyCallLogs = async (queryParam: {
	limit: number | null;
	lastKey?: LastEvaluatedKey;
	startDate?: number | null;
	endDate?: number | null;
	from?: string | null;
}): Promise<{ success: boolean; data?: CallLog[] }> => {
	let queryParamString = '?';

	if (queryParam.limit) {
		queryParamString += `limit=${queryParam.limit}`;
	}

	if (queryParam.lastKey) {
		queryParamString += `&lastKey=${JSON.stringify(queryParam.lastKey)}`;
	}

	if (queryParam.startDate) {
		queryParamString += `&startDate=${queryParam.startDate}`;
	}

	if (queryParam.endDate) {
		queryParamString += `&endDate=${queryParam.endDate}`;
	}

	if (queryParam.from) {
		queryParamString += `&from=${encodeURIComponent(queryParam.from)}`;
	}

	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${COMBINED_WEEKLY_CALL_LOGS_PATH}${queryParamString}`,
	});

	return response;
};

export const getCombinedWeeklyTextLogs = async (): Promise<{
	success: boolean;
	data?: TextLog[];
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${COMBINED_WEEKLY_TEXT_LOGS_PATH}`,
	});

	return response;
};

export const updateIntakeResponse = async (payload: UpdateResponsePayload) => {
	const { agentId, responseId, is_completed, note, status } = payload;
	const endPoint = `/api/v2/receptionist-dashboard/agents/${agentId}/intake-response/${responseId}`;

	const response = await apiClient.request({
		method: 'PATCH',
		endPoint,
		body: { is_completed, note, status },
	});

	return response;
};
